import moment from "moment";

import { LogEventTypeEnum } from "@/Administration/Models/Enums/LogEventTypeEnum";
import { LogAccessReport } from "@/Administration/Models/Log/LogAccessReport";
import { ILogTabModel } from "@/Administration/Models/UI/Logs/ILogTabModel";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";

class AdministrationLogAction extends BaseAction {
    private helper: MyleHelper;

    public constructor() {
        super();
        this.helper = new MyleHelper();
    }

    public loadAdminLogData(model: ILogTabModel): void {
        this.Execute(ServerOperations.Server.Myle_LoadAdminLogData, this.createAdminLogRequestData(model));
    }

    private createAdminLogRequestData(model: ILogTabModel): LogRequestData | LogRequestDataForAccessReport {
        if (model.get("IsAccessReportSelected")) {
            return this.generateLogRequestDataForAccessReport(model);
        }

        return this.generateLogRequestData(model);
    }

    private getAdminLogUsersWhenAccessReportIsChecked(model: ILogTabModel): string[] {
        let users: string[] = [];
        if (!model.get("SelectAllUsersChecked")) {
            users = this.helper
                .GetCheckedItemsFromICheckableList(model.get("UsersGlobalCache"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => (<EmployeeUIModel>x).Model.Contact_id);
        }

        return users;
    }

    private getAdminLogActionTypesWhenAccessReportIsChecked(model: ILogTabModel): string[] {
        let selectedActionTypes: string[] = [];
        if (!model.get("SelectAllActionTypesChecked")) {
            selectedActionTypes = this.helper
                .GetCheckedItemsFromICheckableList(model.get("ActionTypeCache"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => (<CheckboxUIModel>x).Id);
        }

        // PdfGenerated_MedicalSummary and PharmacyPatientConsentedSharingMedicalSummary are grouped under the same action name
        if (selectedActionTypes.includes(LogEventTypeEnum.PdfGenerated_MedicalSummary.toString())
            && !selectedActionTypes.includes(LogEventTypeEnum.PharmacyPatientConsentedSharingMedicalSummary.toString())) {
            selectedActionTypes.push(LogEventTypeEnum.PharmacyPatientConsentedSharingMedicalSummary.toString());
        }

        return selectedActionTypes;
    }

    private getNbAccessAndInterval(model: ILogTabModel): LogAccessReport {
        let logAccessReportParams = new LogAccessReport();
        logAccessReportParams.nbAccess = model.get("NbAccess");
        logAccessReportParams.interval = model.get("Interval");
        logAccessReportParams.intervalType = model.get("IntervalType");

        return logAccessReportParams;
    }

    private generateLogRequestData(model: ILogTabModel): LogRequestData {
        return {
            OrderByColumn: model.get("GridSortColumn"),
            OrderByDirection: model.get("GridSortDirection"),
            EmployeeIds: this.getAdminLogUsersWhenAccessReportIsChecked(model),
            EventTypeList: this.getAdminLogActionTypesWhenAccessReportIsChecked(model),
            SearchText: model.get("LogSearchText"),
            FromDate: model.get("DateFrom") ? moment(model.get("DateFrom")).format("YYYY/MM/DD 00:00:00") : null,
            ToDate: model.get("DateTo") ? moment(model.get("DateTo")).format("YYYY/MM/DD 00:00:00") : null,
            PageNumber: model.get("SelectedPageNumber"),
            PageSize: model.get("SelectedPageSizeOption")
        };
    }

    private generateLogRequestDataForAccessReport(model: ILogTabModel): LogRequestDataForAccessReport {
        const logAccessReportParam = this.getNbAccessAndInterval(model);

        return {
            OrderByColumn: model.get("GridSortColumn"),
            OrderByDirection: model.get("GridSortDirection"),
            SearchText: model.get("LogSearchText"),
            FromDate: model.get("DateFrom") ? moment(model.get("DateFrom")).format("YYYY/MM/DD 00:00:00") : null,
            ToDate: model.get("DateTo") ? moment(model.get("DateTo")).format("YYYY/MM/DD 00:00:00") : null,
            PageNumber: model.get("SelectedPageNumber"),
            PageSize: model.get("SelectedPageSizeOption"),
            NbAccess: logAccessReportParam.nbAccess,
            Interval: logAccessReportParam.interval,
            IntervalType: logAccessReportParam.intervalType
        };
    }
}

interface BaseLogRequestData {
    OrderByColumn: number,
    OrderByDirection: SortDirection,
    SearchText: string,
    FromDate: string | null,
    ToDate: string | null,
    PageNumber: number,
    PageSize: number
}

interface LogRequestData extends BaseLogRequestData {
    EmployeeIds: string[],
    EventTypeList: string[],
}

interface LogRequestDataForAccessReport extends BaseLogRequestData {
    NbAccess: number,
    Interval: number,
    IntervalType: number
}

export { AdministrationLogAction };
